<template>
    <div class="d-flex flex-column">
        <img 
            id="source"
            ref="image"
            :src="images[0] ? images[0].url : ''"
            @load="onImageLoad"
            style="display: none;"
        >
        <!-- Slides -->
        <vueper-slides ref="galleryPicker" class="mb-1"  @slide="onSlideChanged" :style="{paddingBottom: `${heightPercentage}% !important`}" :touchable="false" :arrows="false" :bullets="false" fractions>
            <!-- Images -->
            <vueper-slide class="position-relative" v-for="(image, index) in images" :key="index">
                <template v-slot:content>
                    <div title="Choose an image" class="base-image-input col-12 col-lg-6 p-0" :style="{ 'background-image': `url(${image.url})`, width: '100%', paddingBottom: `${heightPercentage}% !important`, height: '0 !important' }"></div>     
                    <button class="d-flex justify-content-center align-items-center delete-button" @click="remove(index)"><CIcon name="cilMinus"></CIcon></button>
                </template>     
            </vueper-slide>
            <!-- Placeholder -->
            <vueper-slide v-if="!images.length">
                <template v-slot:content>
                    <image-uploader :id="id" ref="fileInput" outputFormat="blob" :maxWidth="maxWidth" :maxHeight="maxHeight" :maxSize="maxSize" :quality="1" :preview="false" accept=".png,.jpg,.jpeg" @input="onFileSelect" className="native-file-input">
                        <label :for="id" slot="upload-label" class="m-0">
                            <div title="Choose an image" class="cursor-pointer base-image-input col-12 col-lg-6 p-0" :style="{ width: '100%', paddingBottom: `${heightPercentage}% !important`, height: '0 !important' }">
                                <span class="d-flex flex-column j placeholder position-absolute">
                                    {{ $t('chooseImage') }}
                                    <pre class="m-0 text-muted">{{readableRatio}}</pre>
                                    <pre class="m-0 text-muted">.png/.jpg/.jpeg</pre>
                                </span>   
                            </div>     
                        </label>
                    </image-uploader>     
                </template>
            </vueper-slide>
        </vueper-slides>
        <!-- Arrows -->
        <!-- <div v-if="images.length && max != 1" class="d-flex justify-content-between mt-1"> -->
        <div v-if="max != 1" class="d-flex justify-content-between mt-1">
            <!-- Previous -->
            <button v-bind:class="{ disabled: images.length == 0 || images.length == 1 }" class="d-flex justify-content-center align-items-center" @click="$refs.galleryPicker.previous()"><CIcon name="cilArrowLeft"></CIcon></button>
            <!-- Change to previous position -->
            <button v-if="allowReorder" v-bind:class="{ disabled: images.length == 0 || images.length == 1 }" class="d-flex justify-content-center align-items-center" @click="reorderImages(-1)"><CIcon name="cilArrowThickFromRight"></CIcon></button>
            <!-- Images -->
            <image-uploader v-if="images.length && (max == 0 || images.length < max)" :id="id" ref="fileInput" outputFormat="blob" :maxWidth="maxWidth" :maxHeight="maxHeight" :maxSize="maxSize" :quality="0.9" :preview="false" accept=".png,.jpg,.jpeg" @input="onFileSelect" className="native-file-input">
                <label :for="id" slot="upload-label" class="position-relative m-0 cursor-pointer">
                    <button class="pointer-none d-flex justify-content-center align-items-center"><CIcon name="cilPlus"></CIcon></button>
                </label>
            </image-uploader> 
            <!-- Change to next position -->
            <button v-if="allowReorder" v-bind:class="{ disabled: images.length == 0 || images.length == 1 }" class="d-flex justify-content-center align-items-center" @click="reorderImages(1)"><CIcon name="cilArrowThickFromLeft"></CIcon></button>
            <!-- Next -->
            <button v-bind:class="{ disabled: !images.length || images.length == 1}" class="d-flex justify-content-center align-items-center" @click="$refs.galleryPicker.next()"><CIcon name="cilArrowRight"></CIcon></button>
        </div>
        <!-- Cropper -->
        <BasePopUp v-if="showModal" v-model="showModal" @detailsClosed="showModal = false">
            <VueCropper v-show="selectedFile" ref="cropper" :src="selectedFile" alt="Source Image" @ready="onCropStart" :aspectRatio="applyRatio" :initialAspectRatio="applyRatio"></VueCropper>
            <CRow class="justify-content-between m-0 p-1">
                <CButton v-show="selectedFile" color="tertiary" text @click="showModal = false">{{ $t('cancel') }}</CButton>

                <!-- New Buttons for Aspect Ratio -->
                <CButton v-show="selectedFile" color="info" @click="setAspectRatio(2, 7)">2:7</CButton>
                <CButton v-show="selectedFile" color="info" @click="setAspectRatio(3, 4)">3:4</CButton>
                <CButton v-show="selectedFile" color="info" @click="setAspectRatio(16, 9)">16:9</CButton>

                <CButton v-show="selectedFile" color="primary" @click="confirmClicked">{{ $t('crop') }}</CButton>
            </CRow>
        </BasePopUp>
    </div>
</template>

<script>
import BasePopUp from "@/controls/popups/BasePopUp.vue";
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import { getReducedFraction } from "@/helpers/utils.js";
import { VueperSlides, VueperSlide } from 'vueperslides'
import 'vueperslides/dist/vueperslides.css'
import ImageUploader from 'vue-image-upload-resize'
import mergeImages from 'merge-images';

const MAX_SIZE = 5; // MB

export default {
    components: { VueCropper, BasePopUp, VueperSlides, VueperSlide, ImageUploader },
        props: {
        value: null, // MAX 1 means object, others is array
        ratioWidth: {
            type: Number,
            default: 2
        },
        ratioHeight: {
            type: Number,
            default: 7
        },
        max: {
            type: Number,
            default: 0 // Infinite
        },
        maxSize:{
            type: Number,
            default: 5120 // 5120 bytes (2MB)
        },
        maxWidth:{
            type: Number,
            default: 1920
        },
        maxHeight:{
            type: Number,
            default: 1080
        },
        allowReorder:{
            type: Boolean,
            default: false
        },
        watermark:
        {
            type: String,
            default: "Watermark"
        }
    },
    data() {
        return {
            mimeType: '',
            fileName: '',
            selectedFile: '',
            showModal: false,
            currentSlide: 0,
            id: "imageUploader" + new Date().valueOf(), // the ID for the file input, required if more than one instance should be used on the same page.
            waterMarkCheckBox: false,
            exportAlwaysJPEG: "image/jpeg",
            images: this.value ? (Array.isArray(this.value) ? this.value : [this.value]) : [],
        }
    },
    computed:
    {
        applyRatio: function()
        {
            return this.ratioWidth / this.ratioHeight;
        },
        readableRatio: function()
        {
            return getReducedFraction(this.ratioWidth, this.ratioHeight);
        },
        heightPercentage: function()
        {
            return this.ratioHeight / this.ratioWidth * 100;
        },
        halfHeightPercentage: function()
        {
            return this.heightPercentage / 2;
        },
    },
    created() {
        window.addEventListener("resize", this.onWindowResize);
    },
    destroyed() {
        window.removeEventListener("resize", this.onWindowResize);
    },
    methods: {
        onImageLoad() {
            const img = this.$refs.image;
            const width = img.naturalWidth;
            const height = img.naturalHeight;

            // Emit the updated aspect ratio
            this.$emit('update:ratioWidth', width);
            this.$emit('update:ratioHeight', height);
        },
        setAspectRatio(width, height) {
            this.$emit('update:ratioWidth', width);
            this.$emit('update:ratioHeight', height);
            this.currentAspectRatio = width / height;
            
            // Update cropper with the new aspect ratio
            this.$refs.cropper.setAspectRatio(this.currentAspectRatio);
        },
        onSlideChanged(currentSlide)
        {
            this.currentSlide = currentSlide;
            this.$emit('currentSlideChanged', currentSlide.currentSlide.index);
        },
        remove(index)
        {
            this.images.splice(index, 1);
        },
        onChooseImageClicked()
        {
            this.$refs.fileInput.click();
        },
        // Note that validation of size and extension is guaranteed by the vue-image-upload-resize plugin
        onFileSelect(blob) 
        {
            try
            {
                // Get file information
                const inputDOM = document.getElementById(this.id);
                const file = inputDOM.files[0];
                if(file.type === "image/png") this.mimeType = this.exportAlwaysJPEG
                else this.mimeType = file.type;
                this.fileName = file.name;
                const maxSize = 1024 * 1024 * MAX_SIZE;

                // Validations
                if(file.size > maxSize)
                {
                    throw this.$t('fileErrorMessage');
                }

                // Load cropper
                const reader = new FileReader()
                reader.onload = (event) => {
                    this.showModal = true;
                    this.selectedFile = event.target.result;
                    this.$nextTick(() => {
                        this.$refs.cropper.replace(this.selectedFile);
                    });
                }
                reader.readAsDataURL(blob);
            }
            catch(err)
            {
                alert(err);
            }
        },
        confirmClicked() {
            // Get the cropped canvas and set the format to JPEG
            let croppedImage = this.$refs.cropper.getCroppedCanvas({ 
                maxWidth: this.maxWidth, 
                maxHeight: this.maxHeight, 
                fillColor: 'white' // Fill transparent areas with white if necessary
            });

            let width = croppedImage.width;
            let height = croppedImage.height;
            let imageData = croppedImage.toDataURL(this.mimeType);
            let images = [imageData];
            
            if(this.waterMarkCheckBox) {
                // PLUGIN https://github.com/csbun/resize-image
                let resizeImage = require('resize-image');
                let image = new Image();
                image.src = "/img/watermark.png";
                image.onload = () => {
                    let data = resizeImage.resize(image, width, height);
                    images.push(data)
                    mergeImages(images, {format: this.mimeType, width: width, height: height })
                    .then(base64Image =>{
                        let newImage = {mime: this.mimeType, data: base64Image.split(',')[1], url: imageData};
                        this.images.push(newImage);
                        this.$refs.galleryPicker.goToSlide(this.images.length - 1);
                    });
                }
            }else{            
                let newImage = {mime: this.mimeType, data: imageData.split(',')[1], url: imageData};
                this.images.push(newImage);
                this.$refs.galleryPicker.goToSlide(this.images.length - 1);
            }

            this.showModal = false;
        },
        setupCanvasAndCrop()
        {
            const cropper = this.$refs.cropper;
 
            if(cropper)
            {
                const image = cropper.getImageData();
                const imageHeight = image.height;
                const imageWidth = image.width;

                const container = document.querySelector(".modal-body > div");
                const containerHeight = container.clientHeight;
                const containerWidth = container.clientWidth;

                if(imageHeight > imageWidth)
                {
                    if(imageHeight > containerHeight)
                    {
                        const height = containerHeight;
                        const width = imageWidth * height / imageHeight;
                        const left = containerWidth / 2 - width / 2;
                        const top = 0;

                        cropper.setCanvasData({ left: left, top:top, width: width, height: height });
                        cropper.setCropBoxData({ left: left, top:top, height: height });
                    }
                }
                // // This logic doesn't seem to be necessary
                // else
                // {
                //     if(imageWidth > containerWidth)
                //     {

                //     }
                // }
            }
        },
        onWindowResize()
        {
            this.setupCanvasAndCrop();
        },
        onCropStart()
        {
            this.setupCanvasAndCrop();
        },
        reorderImages(shift)
        {
            const fromIndex = this.currentSlide.currentSlide.index;
            
            let toIndex = fromIndex + shift;
            if(toIndex < 0) toIndex = this.images.length - 1;
            else if(toIndex > this.images.length - 1) toIndex = 0;

            const element = this.images.splice(fromIndex, 1)[0];
            this.images.splice(toIndex, 0, element);

            this.$refs.galleryPicker.goToSlide(toIndex);
        }
    },
    watch:
    {
        value(val) {
            this.images = val ? (Array.isArray(val) ? val : [val]) : [];
        },
        images(val) {
            const image = val && val.length ? val[0] : null;
            this.$emit('input', this.max == 1 ? image : val);
        },
        showModal: function(val)
        {
            if(!val)
            {
                // this.$refs.fileInput.value=null;
                this.$refs.cropper.reset();
                this.setupCropBox = false;
                this.waterMarkCheckBox = false;
            }
        },
    }
}
</script>

<style scoped>
.delete-button
{
    position: absolute;
    top: 0;
    right: 0;
}

.is-invalid .base-image-input .placeholder
{
    background-color: #e55353;
}

.base-image-input {
  display: block;
  background-size: cover;
  background-position: center center;
  max-width: unset !important;
}

.placeholder {
  background: #F0F0F0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #333;
  font-size: 18px;
  font-family: Helvetica, Arial, Verdana, Tahoma, sans-serif;
}

.placeholder:hover {
  background: #E0E0E0;
}

button
{
    border-color: #d8dbe0;
    border-top-color: rgb(216, 219, 224);
    border-right-color: rgb(216, 219, 224);
    border-bottom-color: rgb(216, 219, 224);
    border-left-color: rgb(216, 219, 224);
    border-radius: 0.25rem;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
}

/deep/.vueperslides
{
    min-width: 250px;
}

/deep/.vueperslides__inner {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
}

/deep/.vueperslides__parallax-wrapper {
    height: 100%;
}

/deep/.vueperslides__parallax-wrapper
{
    padding-bottom: 0 !important;
}

/deep/.cropper-container {
    max-height: 80vh;
}

/deep/.cropper-bg
{
    width: 100% !important;
}

/deep/.native-file-input
{
    display: none;
}

/deep/.native-file-input ~ label
{
    width: 100%;
    height: 100%;
}
</style>